import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { useRef, useState, useEffect } from "react";

export default function ZoomImageSection() {
  const containerRef = useRef(null);
  const videoRef = useRef(null);

  const [isMuted, setIsMuted] = useState(true);
  const [isDesktop, setIsDesktop] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  const VIDEO_URL =
    "https://storage.googleapis.com/agoravoice-hero-videos-bucket/videos/HeroAIVideoV2.mp4";

  /* ------------------ CHECK DESKTOP / MOBILE ------------------ */
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ------------------ INTERSECTION OBSERVER ------------------ */
  useEffect(() => {
    if (!containerRef.current || !videoRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          videoRef.current
            .play()
            .then(() => setIsPlaying(true))
            .catch(() => {});
        } else {
          videoRef.current.pause();
          setIsPlaying(false);
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  /* ------------------ MOBILE USER INTERACTION FIX ------------------ */
  useEffect(() => {
    if (!videoRef.current) return;

    const playVideo = () => {
      videoRef.current
        .play()
        .then(() => {
          setIsPlaying(true);
          document.removeEventListener("touchstart", playVideo);
          document.removeEventListener("click", playVideo);
          document.removeEventListener("scroll", playVideo);
        })
        .catch(() => {});
    };

    document.addEventListener("touchstart", playVideo, { passive: true });
    document.addEventListener("click", playVideo);
    document.addEventListener("scroll", playVideo);

    return () => {
      document.removeEventListener("touchstart", playVideo);
      document.removeEventListener("click", playVideo);
      document.removeEventListener("scroll", playVideo);
    };
  }, []);

  /* ------------------ SCROLL ANIMATION (DESKTOP) ------------------ */
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 200,
    damping: 25,
    mass: 0.5,
  });

  const scale = useTransform(smoothProgress, [0, 1], [0.5, 1.2]);
  const width = useTransform(smoothProgress, [0, 1], ["600px", "100vw"]);
  const borderRadius = useTransform(smoothProgress, [0, 1], [24, 0]);

  /* ------------------ PLAY / PAUSE ------------------ */
  const handlePlayPause = () => {
    if (!videoRef.current) return;

    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  /* ------------------ TOGGLE MUTE ------------------ */
  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  return (
    <section
      ref={containerRef}
      className={`relative bg-[#FFF7EB] ${
        isDesktop ? "min-h-[250vh] py-20" : "pt-20 pb-10"
      }`}
    >
      <div
        className={`${
          isDesktop ? "sticky top-0 h-screen" : "h-auto"
        } flex items-center justify-center overflow-hidden`}
      >
        {/* ------------------ DESKTOP ------------------ */}
        {isDesktop ? (
          <motion.div
            style={{ scale, width, borderRadius }}
            className="aspect-video bg-black shadow-2xl relative overflow-hidden"
          >
            <video
              ref={videoRef}
              src={VIDEO_URL}
              muted={isMuted}
              loop
              playsInline
              preload="auto"
              onClick={handlePlayPause}
              className="w-full h-full object-cover cursor-pointer"
            />

            {/* PLAY BUTTON (SHOW WHEN PAUSED) */}
            {!isPlaying && (
              <button
                onClick={handlePlayPause}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                bg-red-500 rounded-full w-28 h-16 flex items-center justify-center
                shadow-2xl hover:scale-110 transition z-10"
              >
                <svg viewBox="0 0 24 24" fill="#fff" className="w-14 h-14">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
            )}

            {/* MUTE BUTTON */}
            <button
              onClick={toggleMute}
              className="absolute bottom-6 right-6 bg-black/60 text-white px-4 py-2 rounded-full z-10"
            >
              {isMuted ? "Unmute" : "Mute"}
            </button>
          </motion.div>
        ) : (
          /* ------------------ MOBILE ------------------ */
          <div className="w-full max-w-4xl aspect-video bg-black shadow-xl overflow-hidden rounded-2xl relative">
            <video
              ref={videoRef}
              src={VIDEO_URL}
              muted
              loop
              playsInline
              preload="auto"
              onClick={handlePlayPause}
              className="w-full h-full object-cover cursor-pointer"
            />

            {!isPlaying && (
              <button
                onClick={handlePlayPause}
                className="absolute inset-0 flex items-center justify-center"
              >
                <div className="bg-red-500 rounded-full w-20 h-20 flex items-center justify-center shadow-xl">
                  <svg viewBox="0 0 24 24" fill="#fff" className="w-10 h-10">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
